import React from 'react';
import { 
  Droplets, 
  CheckCircle2, 
  Sparkles, 
  RefreshCcw, 
  Plus, 
  Clock
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { WorkoutCutoffCard } from '../dashboard/AntiProcrastinationBanner';

interface WaterCircleProgressProps {
  currentMl: number;
  targetMl: number;
  doseMl: number;
  onAddDose: (amount: number) => void;
  onUndoDose: () => void;
  haConnected?: boolean;
  onSyncHA?: () => void;
  cutoffTime: string;
  workoutDone: boolean;
  onNavigateToWorkout: () => void;
}

export const WaterCircleProgress: React.FC<WaterCircleProgressProps> = ({
  currentMl,
  targetMl,
  doseMl,
  onAddDose,
  onUndoDose,
  haConnected,
  onSyncHA,
  cutoffTime,
  workoutDone,
  onNavigateToWorkout,
}) => {
  const percentage = Math.min(100, Math.round((currentMl / targetMl) * 100));
  const remainingMl = Math.max(0, targetMl - currentMl);
  const dosesConfirmed = Math.floor(currentMl / doseMl);
  const totalDosesRequired = Math.ceil(targetMl / doseMl); // 13 doses

  // 13 doses ao longo do dia (07:00 às 22:00)
  const doseSchedule = [
    { dose: 1, time: '07:00', label: 'Despertar' },
    { dose: 2, time: '08:15', label: 'Pós-Café' },
    { dose: 3, time: '09:30', label: 'Manhã' },
    { dose: 4, time: '10:45', label: 'Pré-Almoço' },
    { dose: 5, time: '12:00', label: 'Almoço' },
    { dose: 6, time: '13:15', label: 'Início Tarde' },
    { dose: 7, time: '14:30', label: 'Pico Metabólico' },
    { dose: 8, time: '15:45', label: 'Pré-Treino' },
    { dose: 9, time: '17:00', label: 'Intra-Treino' },
    { dose: 10, time: '18:15', label: 'Pós-Treino' },
    { dose: 11, time: '19:30', label: 'Janela Noite' },
    { dose: 12, time: '20:45', label: 'Desaceleração' },
    { dose: 13, time: '22:00', label: 'Fechamento 5L' },
  ];

  const [now, setNow] = React.useState<Date>(() => new Date());

  React.useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const getNextDosePrediction = (): string => {
    if (remainingMl <= 0) {
      return `Meta diária de ${targetMl.toLocaleString('pt-BR')} ml concluída`;
    }

    if (dosesConfirmed < doseSchedule.length) {
      const nextItem = doseSchedule[dosesConfirmed];
      const [targetH, targetM] = nextItem.time.split(':').map(Number);
      const targetMinutes = targetH * 60 + targetM;
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const diffMinutes = targetMinutes - currentMinutes;

      if (diffMinutes > 60) {
        const hours = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;
        const timeStr = mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
        return `Próxima dose: ${nextItem.time} (em ${timeStr}) • ${doseMl} ml`;
      }
      if (diffMinutes > 0) {
        return `Próxima dose: ${nextItem.time} (em ${diffMinutes} min) • ${doseMl} ml`;
      }
      if (diffMinutes === 0) {
        return `Próxima dose: ${nextItem.time} (agora) • ${doseMl} ml`;
      }
      // Horário passou
      if (currentMinutes >= 22 * 60) {
        return `Próxima dose: 07:00 • Restam ${remainingMl.toLocaleString('pt-BR')} ml`;
      }
      return `Dose ${nextItem.dose} pendente: ${nextItem.time} • ${doseMl} ml`;
    }

    return `Dose adicional pendente: ${remainingMl.toLocaleString('pt-BR')} ml`;
  };

  const handleConfirmDose = (amount = doseMl) => {
    onAddDose(amount);

    try {
      confetti({
        particleCount: 50,
        spread: 70,
        origin: { y: 0.75 },
        colors: ['#00f0ff', '#38bdf8', '#ffffff', '#a855f7'],
      });
    } catch (e) {}

    if ('vibrate' in navigator) {
      navigator.vibrate([70, 40, 90]);
    }
  };

  // Parâmetros do SVG Circular
  const radius = 100;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="space-y-4 max-w-xl mx-auto pb-28 pt-1">
      {/* 1. PRIMEIRO CARD: Horário Limite do Treino (Design iOS HIG) */}
      <WorkoutCutoffCard
        cutoffTime={cutoffTime}
        workoutDone={workoutDone}
        onNavigateToWorkout={onNavigateToWorkout}
      />

      {/* 3. SEGUNDO CARD: Anel de Hidratação Liquid Glass Apple Watch Style */}
      <div className="liquid-glass-elevated rounded-[34px] p-6 text-center relative overflow-hidden">
        {/* Glow de Fundo Sutil Líquido */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-tr from-[#0A84FF]/15 via-[#30D6FB]/12 to-transparent rounded-full blur-3xl pointer-events-none" />

        {/* Status de Sincronização com Home Assistant */}
        <div className="flex items-center justify-between mb-2 px-1 relative z-10">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${haConnected ? 'bg-[#30D158] shadow-[0_0_8px_#30D158]' : 'bg-[#FF9F0A] animate-pulse'}`} />
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              {haConnected ? 'Home Assistant Conectado' : 'Sincronizando HA...'}
            </span>
          </div>
          {onSyncHA && (
            <button
              onClick={onSyncHA}
              className="text-[11px] font-bold text-[#0A84FF] dark:text-[#30D6FB] flex items-center gap-1 hover:opacity-80 transition cursor-pointer apple-press"
              title="Sincronizar agora com Home Assistant"
            >
              <RefreshCcw className="w-3 h-3" />
              <span>Sincronizar</span>
            </button>
          )}
        </div>

        {/* Anel de Atividade do Apple Watch */}
        <div className="relative w-64 h-64 mx-auto flex items-center justify-center my-2">
          <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_24px_rgba(48,214,251,0.35)]" viewBox="0 0 240 240">
            <defs>
              <linearGradient id="appleActivityCyan" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0A84FF" />
                <stop offset="50%" stopColor="#30D6FB" />
                <stop offset="100%" stopColor="#63E6E2" />
              </linearGradient>
            </defs>

            {/* Trilha do Anel Desativada (Cinza Líquido Apple) */}
            <circle
              cx="120"
              cy="120"
              r={radius}
              stroke="rgba(120, 120, 128, 0.15)"
              strokeWidth="18"
              fill="transparent"
            />

            {/* Trilha de Progresso com Gradiente Fluido Apple */}
            <circle
              cx="120"
              cy="120"
              r={radius}
              stroke="url(#appleActivityCyan)"
              strokeWidth="18"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              fill="transparent"
              className="transition-all duration-700 ease-out"
            />
          </svg>

          {/* Dados Centrais com Tipografia SF Pro */}
          <div className="absolute flex flex-col items-center justify-center">
            {/* Pill de Status da Dose */}
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#007AFF]/10 dark:bg-[#30D6FB]/15 border border-[#007AFF]/25 dark:border-[#30D6FB]/30 text-[#007AFF] dark:text-[#30D6FB] text-[11px] font-bold tracking-tight mb-1.5 shadow-sm dark:shadow-[0_0_12px_rgba(48,214,251,0.2)]">
              <Droplets className="w-3 h-3 fill-current" />
              <span>Dose {dosesConfirmed + 1} de {totalDosesRequired}</span>
            </div>

            {/* Volume em SF Pro Tabular */}
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white font-sans tabular-nums drop-shadow-sm dark:drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                {currentMl.toLocaleString('pt-BR')}
              </span>
              <span className="text-sm font-semibold text-slate-400 dark:text-white/50">ml</span>
            </div>

            {/* Meta */}
            <div className="text-xs font-medium text-slate-500 dark:text-white/45 mt-1">
              Meta: <span className="text-slate-900 dark:text-white/90 font-bold">{targetMl.toLocaleString('pt-BR')} ml</span>
            </div>

            {/* Percentual em Cápsula Liquid Glass */}
            <div className="mt-2.5">
              <span className="text-[11px] font-extrabold tracking-wider uppercase px-3.5 py-1 rounded-full bg-gradient-to-r from-[#0A84FF] to-[#30D6FB] text-black font-sans shadow-[0_2px_16px_rgba(48,214,251,0.45)]">
                {percentage}% Concluído
              </span>
            </div>
          </div>
        </div>

        {/* Previsão da Próxima Dose (Informação Direta e Factual) */}
        <div className="text-xs font-semibold text-slate-600 dark:text-white/70 mb-5 flex items-center justify-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-[#007AFF] dark:text-[#30D6FB] shrink-0" />
          <span className="tabular-nums">{getNextDosePrediction()}</span>
        </div>

        {/* BOTÃO PRINCIPAL: CONFIRMAR DOSE (SÓLIDO APPLE CYAN) */}
        <div className="space-y-3">
          <button
            onClick={() => handleConfirmDose(doseMl)}
            className="w-full py-6 px-8 rounded-[28px] bg-[#30D6FB] hover:brightness-105 text-black font-black text-3xl uppercase tracking-wider shadow-[0_8px_24px_rgba(48,214,251,0.35)] transition-all apple-press flex items-center justify-center gap-3.5 cursor-pointer"
          >
            <Droplets className="w-9 h-9 fill-black" />
            <span>Confirmar (+{doseMl}ml)</span>
          </button>

          {/* Micro-Ajustes Rápidos em Pílulas Sólidas */}
          <div className="grid grid-cols-3 gap-2.5 pt-1">
            <button
              onClick={() => handleConfirmDose(200)}
              className="py-3.5 px-3 rounded-[20px] bg-slate-100 hover:bg-slate-200 dark:bg-[#1C1C1E] dark:hover:bg-[#2C2C2E] border border-black/[0.06] dark:border-white/[0.08] text-xs font-bold text-slate-800 dark:text-white transition apple-press flex items-center justify-center gap-1 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 text-[#007AFF] dark:text-[#30D6FB]" />
              <span>+200ml</span>
            </button>

            <button
              onClick={() => handleConfirmDose(500)}
              className="py-3.5 px-3 rounded-[20px] bg-slate-100 hover:bg-slate-200 dark:bg-[#1C1C1E] dark:hover:bg-[#2C2C2E] border border-black/[0.06] dark:border-white/[0.08] text-xs font-bold text-slate-800 dark:text-white transition apple-press flex items-center justify-center gap-1 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 text-[#007AFF] dark:text-[#30D6FB]" />
              <span>+500ml</span>
            </button>

            <button
              onClick={onUndoDose}
              disabled={currentMl <= 0}
              className="py-3.5 px-3 rounded-[20px] bg-slate-100 hover:bg-slate-200 dark:bg-[#1C1C1E] dark:hover:bg-[#2C2C2E] border border-black/[0.06] dark:border-white/[0.08] text-xs font-bold text-slate-500 hover:text-slate-900 dark:text-white/50 dark:hover:text-white transition apple-press disabled:opacity-30 flex items-center justify-center gap-1 shadow-sm"
              title="Desfazer última dose"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              <span>Desfazer</span>
            </button>
          </div>
        </div>
      </div>

      {/* 4. TERCEIRO CARD: Grade Cronometrada (Liquid Glass Inset List) */}
      <div className="liquid-glass rounded-[32px] p-5 space-y-3">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#007AFF] dark:text-[#30D6FB]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-white/70">
              Cronograma de Doses
            </h3>
          </div>
          <span className="text-[11px] font-mono text-[#007AFF] dark:text-[#30D6FB] font-semibold">
            {dosesConfirmed} de {totalDosesRequired} Concluídas
          </span>
        </div>

        {/* Linhas da Lista Agrupada */}
        <div className="space-y-1.5">
          {doseSchedule.map((item) => {
            const isDone = item.dose <= dosesConfirmed;
            const isCurrent = item.dose === dosesConfirmed + 1;

            return (
              <div
                key={item.dose}
                onClick={() => {
                  if (!isDone) handleConfirmDose(doseMl);
                }}
                className={`p-3.5 rounded-[20px] border transition-all duration-200 flex items-center justify-between cursor-pointer apple-press ${
                  isDone
                    ? 'bg-[#007AFF]/10 dark:bg-[#30D6FB]/12 border-[#007AFF]/25 dark:border-[#30D6FB]/30 text-[#007AFF] dark:text-[#30D6FB] shadow-sm'
                    : isCurrent
                    ? 'bg-gradient-to-r from-[#FF9F0A]/25 to-[#FFD60A]/15 border-[#FF9F0A]/60 text-[#FF9F0A] shadow-[0_4px_20px_rgba(255,159,10,0.2),inset_0_1px_0_rgba(255,255,255,0.4)] animate-pulse'
                    : 'bg-black/[0.02] dark:bg-white/[0.03] border-black/[0.05] dark:border-white/[0.06] text-slate-700 dark:text-white/50 hover:bg-black/[0.05] dark:hover:bg-white/[0.07] hover:text-slate-900 dark:hover:text-white/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-[12px] flex items-center justify-center text-xs font-mono font-bold ${
                      isDone
                        ? 'bg-[#007AFF]/20 dark:bg-[#30D6FB]/25 text-[#007AFF] dark:text-[#30D6FB] border border-[#007AFF]/30 dark:border-[#30D6FB]/40'
                        : isCurrent
                        ? 'bg-[#FF9F0A]/30 text-[#FF9F0A] border border-[#FF9F0A]/60 shadow-[0_0_12px_rgba(255,159,10,0.3)]'
                        : 'bg-black/[0.05] dark:bg-white/[0.06] text-slate-500 dark:text-white/40 border border-black/[0.08] dark:border-white/[0.08]'
                    }`}
                  >
                    {isDone ? '✓' : item.dose}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-slate-900 dark:text-white tracking-tight">{item.time}</span>
                      <span className="text-[11px] text-slate-600 dark:text-white/70 font-medium">• {item.label}</span>
                    </div>
                    <span className="text-[10px] text-slate-400 dark:text-white/40 font-mono">Dose: 400ml</span>
                  </div>
                </div>

                <div className="text-right">
                  <span
                    className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                      isDone
                        ? 'bg-[#007AFF]/15 dark:bg-[#30D6FB]/20 text-[#007AFF] dark:text-[#30D6FB] border border-[#007AFF]/30 dark:border-[#30D6FB]/35'
                        : isCurrent
                        ? 'bg-[#FF9F0A]/30 text-[#FF9F0A] border border-[#FF9F0A]/60 font-black shadow-[0_0_10px_rgba(255,159,10,0.2)]'
                        : 'bg-black/[0.04] dark:bg-white/[0.05] text-slate-500 dark:text-white/40 border border-black/[0.06] dark:border-white/[0.06]'
                    }`}
                  >
                    {isDone ? 'Concluída' : isCurrent ? 'Beber Agora' : 'Pendente'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
